import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, Zap, Trophy, Users, BookOpen, Target, Download, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CounterCard from '@/components/CounterCard';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ active_users: 12547, prompts_created: 85432, downloads: 3891, user_rating: 4.9 });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/stats`);
        const data = await response.json();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleGetStarted = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleInstallClick = async () => {
    if (isIOS) {
      toast.info('Tap the Share button, then "Add to Home Screen"', { duration: 5000 });
      return;
    }

    if (!deferredPrompt) {
      toast.info('App is already installed or your browser doesn\'t support installation');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen relative" data-testid="landing-page">
      {/* AI-themed background elements */}
      <div className="neural-network-bg"></div>
      <div className="ai-brain-pattern"></div>
      
      {/* Floating orbs */}
      <div className="fixed top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}}></div>
      <div className="fixed bottom-20 right-10 w-40 h-40 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{animationDuration: '6s', animationDelay: '2s'}}></div>
      <div className="fixed top-1/2 right-1/4 w-24 h-24 bg-primary/10 rounded-full blur-2xl animate-pulse" style={{animationDuration: '5s', animationDelay: '1s'}}></div>
      
      <nav className="px-6 py-4 flex justify-between items-center bg-white/95 backdrop-blur-sm border-b border-border shadow-sm relative z-10">
        <div className="flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">TerryLBPrompts</h1>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost"
            onClick={() => navigate('/pricing')}
            className="text-foreground hover:text-primary font-medium"
            data-testid="pricing-link"
          >
            Pricing
          </Button>
          <Button 
            onClick={handleGetStarted}
            className="bg-primary text-white hover:bg-primary/90 rounded-lg px-6 font-semibold shadow-md hover:shadow-lg transition-all"
          >
            Get Started
          </Button>
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 py-16 relative z-10" data-testid="hero">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left side - Text content */}
          <div className="text-left">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight text-foreground">
              Master
              <br />
              <span className="text-primary">Prompt Engineering</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-muted-foreground leading-relaxed">
              Create perfect prompts for ChatGPT, Midjourney, DALL-E, Sora & more.
              Professional tools for prompt crafting excellence.
            </p>
            <Button 
              onClick={handleGetStarted}
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 text-lg px-10 py-6 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="cta-button"
            >
              <Sparkles className="mr-2 h-6 w-6" />
              Start Learning Free
          </Button>
          </div>
          
          {/* Right side - Terry's Image */}
          <div className="flex justify-center md:justify-end">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur-2xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_61ae778b-841c-413e-9b69-d92062653a37/artifacts/vodftduj_Gemini_Generated_Image_saixqysaixqysaix.png"
                alt="Terry LB - Your AI Prompt Expert"
                className="relative w-full max-w-md rounded-2xl shadow-2xl border-4 border-white/50"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          <CounterCard end={stats.active_users} label="Active Users" color="text-primary" />
          <CounterCard end={stats.prompts_created} label="Prompts Created" color="text-green-600" />
          <CounterCard end={stats.downloads} label="Downloads" color="text-primary" />
          <CounterCard end={stats.user_rating * 1000} label="User Rating" color="text-green-600" />
        </div>

        {/* PWA Install Section */}
        {!isInstalled && (
          <div className="mt-16 max-w-2xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/20 text-center">
              <Smartphone className="h-16 w-16 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-3 text-foreground">Install as Mobile App</h3>
              <p className="text-muted-foreground mb-6">
                Add TerryLBPrompts to your home screen for quick access and an app-like experience!
              </p>
              <Button 
                onClick={handleInstallClick}
                size="lg"
                className="bg-primary text-white hover:bg-primary/90 px-8 py-6 text-lg font-semibold shadow-lg"
              >
                <Download className="mr-2 h-5 w-5" />
                {isIOS ? 'Install Instructions' : 'Install App'}
              </Button>
              {isIOS && (
                <p className="text-sm text-muted-foreground mt-4">
                  📱 iPhone: Tap Share <span className="inline-block">⎙</span> → Add to Home Screen
                </p>
              )}
            </Card>
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 relative z-10">
        <h2 className="text-5xl font-bold text-center mb-4 text-foreground">Everything You Need</h2>
        <p className="text-center text-xl text-muted-foreground mb-16 max-w-2xl mx-auto">Comprehensive tools and resources to master AI prompt engineering</p>
        
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-8 bg-white border border-border card-shadow-lg hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
              <Zap className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-foreground">Prompt Builder</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Simple Q&A wizard generates perfect prompts for any AI tool in seconds.</p>
          </Card>

          <Card className="p-8 bg-white border border-border card-shadow-lg hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-green-500/10 rounded-xl flex items-center justify-center mb-6">
              <Target className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-foreground">Daily Challenges</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Practice daily, get AI-powered feedback, and watch your skills improve.</p>
          </Card>

          <Card className="p-8 bg-white border border-border card-shadow-lg hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
              <Trophy className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-foreground">Progress Tracking</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Earn badges, level up, and compete on leaderboards. Stay motivated!</p>
          </Card>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 relative z-10">
        <div className="bg-gradient-to-r from-primary to-accent p-12 rounded-3xl text-center shadow-2xl">
          <h2 className="text-5xl font-bold text-white mb-6">Ready to Master AI Prompts?</h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">Join thousands of creators learning to craft perfect prompts for AI tools</p>
          <Button 
            onClick={handleGetStarted}
            size="lg"
            className="bg-white text-primary hover:bg-white/90 text-lg px-12 py-6 rounded-lg font-bold shadow-xl hover:shadow-2xl transition-all"
          >
            <Sparkles className="mr-2 h-6 w-6" />
            Start Free Today
          </Button>
        </div>
      </section>

      <footer className="relative z-10 bg-white/95 backdrop-blur-sm border-t border-border mt-20">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-3 gap-12 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold text-foreground">TerryLBPrompts</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">Master the art of AI prompt engineering with our comprehensive platform.</p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-foreground">Features</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Prompt Builder</li>
                <li className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Community Library</li>
                <li className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /> Mini Lessons</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-foreground">Support</h4>
              <p className="text-muted-foreground mb-2">Need help? Contact us:</p>
              <a href="mailto:computerwork2022@gmail.com" className="text-primary hover:underline font-medium">computerwork2022@gmail.com</a>
            </div>
          </div>
          
          <div className="border-t border-border pt-6 text-center">
            <p className="text-sm text-muted-foreground">&copy; 2025 TerryLBPrompts. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
